import React from 'react'
import { ActionPlan, ActionPlanStatus, Cost, CostCategory, FinancialYear, FinancialYearDisplayDTO, UserAccount } from '../api/schemas/schema'
import { IListState } from '../interfaces/IListState'
import { Checkbox, TableColumnDefinition, tokens } from '@fluentui/react-components'
import { t } from '../i18n/i18n'
import { getFullName } from './accountHelper'
import { IDataContext } from '../interfaces/IDataContext'
import { handleMessage } from './stateHelper'
import { api } from './apiHelper'
import { translateEnumValue } from './enumHelper'
import { compareByDate, formatDate, summarizeFieldValues } from './stringHelper'
import { BadgeCostCategory } from '../components/riskEconomy/BadgeCostCategory'
import { Text } from '../components/common/text/Text'
import { ISummaryField } from '../interfaces/ISummaryField'
import { IItemState } from '../interfaces/IItemState'
import { UserAvatarOutput } from '../components/common/inputs/UserAvatarOutput'
import { IGroup } from '../interfaces/IGroup'
import { DeviationSummary } from '../components/riskEconomy/DeviationSummary'
import { IGroupedTableColumnDefintion } from '../interfaces/IGroupedTableColumnDefintion'
import { BadgeAmountCircle } from '../components/riskEconomy/BadgeAmountCircle'
import { FormattedMoneyOutput } from '../components/riskEconomy/FormattedMoneyOutput'
import { MoreOptionsMenuButton } from '../components/common/other/MoreOptionsMenuButton'
import { isFinancialYearOwnerOrCreator } from './financialYearHelper'
import { isSystemAdmin } from './permissionHelper'

export enum RiskEconomyTabs {
   Ongoing = 'Ongoing',
   Concluded = 'Concluded',
}

export enum FinancialYearTabs {
   Info = 'info',
   IdentifyCosts = 'identify-costs',
   Evaluation = 'evaluation',
   ActionPlan = 'actionplan',
}

export const addFinancialYear = async (
   setRiskEconomyState: React.Dispatch<React.SetStateAction<IListState<FinancialYear>>>,
   dataContext: IDataContext,
   data: FinancialYear
) => {
   const { userAccount, organisation } = dataContext.state
   try {
      data.createdBy = userAccount
      data.createdById = userAccount.id
      data.modifiedBy = userAccount
      data.modifiedById = userAccount.id
      data.organisationId = organisation.id

      const addedFinancialYear = (await api().addFinancialYear(data)).data

      setRiskEconomyState((prev) => ({
         ...prev,
         allItems: [...prev.allItems, addedFinancialYear],
      }))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyCreated', { Item: t('FinancialYear') }))

      return addedFinancialYear
   } catch (error: any) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotCreateItem', { Item: t('FinancialYear') }), error.error.message)
   }
}

export const updateFinancialYear = async (
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>,
   dataContext: IDataContext,
   data: FinancialYear
) => {
   const { userAccount } = dataContext.state
   try {
      data.modifiedBy = userAccount
      data.modifiedById = userAccount.id

      const updatedFinancialYear = (await api().updateFinancialYear(data)).data

      setFinancialYearState((prev) => ({
         ...prev,
         item: updatedFinancialYear,
      }))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('FinancialYear') }))
   } catch (error: any) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotUpdateItem', { Item: t('FinancialYear') }), error.error.message)
   }
}

export const deleteFinancialYear = async (
   id: string,
   dataContext: IDataContext,
   setRiskEconomyState: React.Dispatch<React.SetStateAction<IListState<FinancialYear>>>
) => {
   try {
      await api().deleteFinancialYear(id)

      setRiskEconomyState((prev) => ({
         ...prev,
         allItems: prev.allItems.filter((item) => item.id !== id),
         filteredItems: prev.filteredItems.filter((item) => item.id !== id),
         open: false,
      }))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('FinancialYear') }))
   } catch (error: any) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('FinancialYear') }), error.error.message)
   }
}

export const updateCostOnFinancialYear = async (
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>,
   dataContext: IDataContext,
   data: Cost,
   financialYearId: string
) => {
   const { userAccount } = dataContext.state
   try {
      data.modifiedBy = userAccount
      data.modifiedById = userAccount.id

      const response = (await api().updateCostOnFinancialYear(financialYearId, data)).data

      const financialYear = response.updatedFinancialYear
      const updatedCost = financialYear.costs.find((c) => c.id === data.id)
      const createdActionPlan = response.actionPlan

      if (createdActionPlan == null) {
         financialYear.actionPlans = financialYear.actionPlans.filter((ap) => ap.riskId !== updatedCost.id)
      }

      setFinancialYearState((prev) => ({ ...prev, item: financialYear }))

      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('Cost') }))
   } catch (error: any) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotUpdateItem', { Item: t('Cost') }), error.error.message)
   }
}

export const createOrUpdateFinancialYearActionPlan = async (
   data: ActionPlan,
   financialYear: FinancialYear,
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
) => {
   try {
      const response = (await api().createOrUpdateFinancialYearActionPlan(financialYear.organisationId, financialYear.id, data)).data
      const updatedFinancialYear = response.updatedFinancialYear
      const createdActionPlan = response.actionPlan

      if (createdActionPlan == null) {
         updatedFinancialYear.actionPlans = updatedFinancialYear.actionPlans.filter((ap) => ap.id !== data.id)
      }

      setFinancialYearState((prev) => ({
         ...prev,
         item: {
            ...prev.item,
            ...updatedFinancialYear,
         },
      }))

      return true
   } catch (error: any) {
      return false
   }
}

export const updateActionPlan = async (
   data: ActionPlan,
   parent: FinancialYear,
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
) => {
   try {
      const updatedFinancialYear = (await api().updateActionPlanOnFinancialYear(parent.id, data)).data
      setFinancialYearState((prev) => ({ ...prev, item: updatedFinancialYear }))
      return true
   } catch (error: any) {
      return false
   }
}

export const getRiskEconomyColors = (costCategory: CostCategory) => {
   switch (costCategory) {
      case CostCategory.DamageCosts:
         return tokens.colorStatusDangerBackground3
      case CostCategory.InsuranceCosts:
         return '#919191'
      case CostCategory.PreventiveCosts:
         return '#394B5F'
      case CostCategory.SecurityAdministration:
         return tokens.colorPaletteMarigoldBackground3
   }
}

export const getRiskEconomyColumns = (
   setRiskEconomyState: React.Dispatch<React.SetStateAction<IListState<FinancialYear>>>,
   userAccount: UserAccount
): TableColumnDefinition<FinancialYearDisplayDTO>[] => [
   {
      columnId: 'name',
      renderHeaderCell: () => t('Name'),
      renderCell: (item) => item.name,
      compare: (a, b) => a.name.localeCompare(b.name),
   },
   {
      columnId: 'owner',
      renderHeaderCell: () => t('Owner'),
      renderCell: (item) => <UserAvatarOutput userAccount={item.owner} />,
      compare: (a, b) => getFullName(a.owner).localeCompare(getFullName(b.owner)),
   },
   {
      columnId: 'period',
      renderHeaderCell: () => t('Period'),
      renderCell: (item) => {
         return `${formatDate(item.startDate)} - ${formatDate(item.endDate)}`
      },
      compare: (a, b) => compareByDate(a.startDate, b.startDate),
   },
   {
      columnId: 'menu',
      renderHeaderCell: () => <div style={{ width: '35px' }}>&nbsp;</div>,
      renderCell: (item) => (
         <MoreOptionsMenuButton
            disabled={!isFinancialYearOwnerOrCreator(item, userAccount) && !isSystemAdmin(userAccount)}
            disabledToolTipMessage={t('DeleteFinancialYearNoAccessMessage')}
            item={item}
            deleteFunction={deleteFinancialYear}
            setState={setRiskEconomyState}
            deleteMessage={t('DeleteItem', { Item: t('FinancialYear').toLowerCase() })}
            deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('TheFinancialYear').toLocaleLowerCase() })}
         />
      ),
      compare: () => 0,
   },
]

export const getCostIdentifyingColumns = (): TableColumnDefinition<Cost>[] => [
   {
      columnId: 'date',
      renderHeaderCell: () => t('Date'),
      renderCell: (item) => formatDate(item.created),
      compare: (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
   },
   {
      columnId: 'category',
      renderHeaderCell: () => t('Category'),
      renderCell: (item) => <BadgeCostCategory category={item.post.costCategory} />,
      compare: (a, b) => {
         const aCategory = translateEnumValue(CostCategory, a.post.costCategory)
         const bCategory = translateEnumValue(CostCategory, b.post.costCategory)
         return aCategory.localeCompare(bCategory)
      },
   },
   {
      columnId: 'post',
      renderHeaderCell: () => t('Post'),
      renderCell: (item) => item.post.name ?? '-',
      compare: (a, b) => {
         const aValue = a.post.name ? a.post.name : '-'
         const bValue = b.post.name ? b.post.name : '-'
         return aValue.localeCompare(bValue)
      },
   },
   {
      columnId: 'description',
      renderHeaderCell: () => t('Description'),
      renderCell: (item) =>
         item.description ? (
            <Text ellipsis style={{ WebkitLineClamp: 3 }} size={300}>
               {item.description}
            </Text>
         ) : (
            <i>{t('NoValueSet')}</i>
         ),
      compare: (a, b) => a.description.localeCompare(b.description),
   },
   {
      columnId: 'budget',
      renderHeaderCell: () => t('Budget'),
      renderCell: (item) => <FormattedMoneyOutput size={300} value={item.budget} />,
      compare: (a, b) => a.budget - b.budget,
   },
   {
      columnId: 'result',
      renderHeaderCell: () => t('Result'),
      renderCell: (item) => <FormattedMoneyOutput size={300} value={item.result} />,
      compare: (a, b) => a.result - b.result,
   },
]
export const getCostSummaryFields = (costs: Cost[]): ISummaryField[] => [
   {
      label: t('TotalNumberOfPosts'),
      text: costs.length,
      alignStart: true,
   },
   {
      label: t('TotalBudget'),
      text: <FormattedMoneyOutput value={summarizeFieldValues(costs, 'budget')} />,
   },
   {
      label: t('TotalResult'),
      text: <FormattedMoneyOutput value={summarizeFieldValues(costs, 'result')} />,
   },
]

export const getCostEvaluationSummaryFields = (costs: Cost[]): ISummaryField[] => [
   {
      label: t('TotalNumberOfPosts'),
      text: costs.length,
      alignStart: true,
   },
   {
      label: t('TotalBudget'),
      text: <FormattedMoneyOutput value={summarizeFieldValues(costs, 'budget')} />,
   },
   {
      label: t('TotalResult'),
      text: <FormattedMoneyOutput value={summarizeFieldValues(costs, 'result')} />,
   },
   {
      label: t('SummaryDeviation'),
      text: <DeviationSummary costs={costs} />,
   },
]

export const getCostEvaluationGroups = (costs: Cost[]): IGroup<Cost>[] => {
   const groups: IGroup<Cost>[] = []
   costs.forEach((cost) => {
      const key = cost.post.costCategory.toString()
      const label = translateEnumValue(CostCategory, cost.post.costCategory)

      const group = groups.find((g) => g.key === key)

      if (!group) {
         groups.push({ label, key, order: cost.post.costCategory, hex: '', expanded: true, items: [] })
      }
      groups.find((g) => g.key === key).items.push(cost)
   })
   return groups.sort((a, b) => a.order - b.order)
}

export const getCostEvaluationColumns = (disabled: boolean, financialYear: FinancialYear): IGroupedTableColumnDefintion<Cost>[] => [
   {
      columnId: 'post',
      colWidth: '200px',
      renderHeaderCell: () => t('Post'),
      renderGroupHeaderCell: (group) => {
         return <BadgeCostCategory category={Number(group.key)} />
      },
      renderCell: (cost) => {
         return cost.post.name ? cost.post.name : translateEnumValue(CostCategory, cost.post.costCategory)
      },
      compare: (a, b) => {
         const aValue = a.post.name ? a.post.name : translateEnumValue(CostCategory, a.post.costCategory)
         const bValue = b.post.name ? b.post.name : translateEnumValue(CostCategory, b.post.costCategory)
         return aValue.localeCompare(bValue)
      },
   },
   {
      columnId: 'numberOfPosts',
      colWidth: 'auto',
      align: 'left',
      renderHeaderCell: () => t('NumberOfPosts'),
      renderCell: () => {
         return null
      },
      renderGroupHeaderCell: (group) => {
         return <BadgeAmountCircle amount={group.items.length} />
      },
      compare: () => -1,
   },
   {
      columnId: 'budget',
      colWidth: 'auto',
      align: 'left',
      renderHeaderCell: () => t('Budget'),
      renderCell: (item) => <FormattedMoneyOutput size={300} value={item.budget} />,
      renderGroupHeaderCell: (group) => <FormattedMoneyOutput size={300} value={summarizeFieldValues(group.items, 'budget')} />,
      compare: () => -1,
   },
   {
      columnId: 'result',
      colWidth: 'auto',
      align: 'left',
      renderHeaderCell: () => t('Result'),
      renderCell: (item) => <FormattedMoneyOutput size={300} value={item.result} />,
      renderGroupHeaderCell: (group) => <FormattedMoneyOutput size={300} value={summarizeFieldValues(group.items, 'result')} />,
      compare: () => -1,
   },
   {
      columnId: 'deviation',
      colWidth: 'auto',
      align: 'left',
      renderHeaderCell: () => t('Deviation'),
      renderCell: (item) => <DeviationSummary size={300} cost={item} />,
      renderGroupHeaderCell: (group) => {
         return <DeviationSummary size={300} costs={group.items} />
      },
      compare: () => -1,
   },
   {
      columnId: 'actionRequired',
      colWidth: 'auto',
      align: 'left',
      renderHeaderCell: () => t('ProposedAction'),
      renderCell: (cost) => {
         const actionPlan = financialYear.actionPlans.find((ap) => ap.costId === cost?.id)
         const isDisabled = disabled || (actionPlan && actionPlan.status !== ActionPlanStatus.NotDecided)

         return <Checkbox checked={cost.actionRequired} disabled={isDisabled} />
      },
      renderGroupHeaderCell: () => {
         return null
      },
      compare: () => -1,
   },
]

export const getGroupedAndSummarizedCosts = (costs: Cost[]) => {
   const groupedCosts = {} as { [key: number]: { name: string; costCategory: CostCategory; result: number; budget: number } }

   costs.forEach((cost) => {
      const category = cost.post.costCategory

      if (!groupedCosts[category]) {
         groupedCosts[category] = {
            name: translateEnumValue(CostCategory, category),
            costCategory: category,
            result: cost.result,
            budget: cost.budget,
         }
      } else {
         groupedCosts[category].result += cost.result
         groupedCosts[category].budget += cost.budget
      }
   })

   return Object.values(groupedCosts).sort((a, b) => a.costCategory - b.costCategory)
}
